import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Gift, Star } from 'lucide-react';
interface UpsellOption {
  id: string;
  title: string;
  description: string;
  price: number;
  icon: string;
}
interface UpsellPopupProps {
  isOpen: boolean;
  onClose: () => void;
  basePlanPrice: number;
  basePlanName: string;
  showUpgradeOption: boolean;
}
export function UpsellPopup({
  isOpen,
  onClose,
  basePlanPrice,
  basePlanName,
  showUpgradeOption
}: UpsellPopupProps) {
  const [selectedOptions, setSelectedOptions] = useState<Set<string>>(new Set());
  const allUpsellOptions: UpsellOption[] = [{
    id: 'upgrade',
    title: '⭐ Upgrade a Plan Completo',
    description: 'Accedé a los 8 módulos + 3 bonos exclusivos',
    price: 5000,
    icon: '🏆'
  }, {
    id: 'gerontology',
    title: '👴 Especialidad en Gerontología',
    description: 'Módulo especializado para trabajo con adultos mayores',
    price: 6000,
    icon: '📚'
  }, {
    id: 'children',
    title: '👶 Especialidad en Infancias y Adolescencia',
    description: 'Módulo especializado para trabajo con niños y adolescentes con espectro autista o tdah.',
    price: 6000,
    icon: '🎨'
  }];
  // Filter options based on showUpgradeOption prop
  const upsellOptions = showUpgradeOption ? allUpsellOptions : allUpsellOptions.filter(option => option.id !== 'upgrade');
  const toggleOption = (optionId: string) => {
    const newSelected = new Set(selectedOptions);
    if (newSelected.has(optionId)) {
      newSelected.delete(optionId);
    } else {
      newSelected.add(optionId);
    }
    setSelectedOptions(newSelected);
  };
  const calculateTotal = () => {
    let total = basePlanPrice;
    selectedOptions.forEach(optionId => {
      const option = upsellOptions.find(o => o.id === optionId);
      if (option) {
        total += option.price;
      }
    });
    return total;
  };
  const getButtonText = () => {
    const hasUpgrade = selectedOptions.has('upgrade');
    const hasGerontology = selectedOptions.has('gerontology');
    const hasChildren = selectedOptions.has('children');
    if (hasUpgrade && hasGerontology && hasChildren) {
      return 'Continuar con Pack Completo + 2 Especialidades';
    } else if (hasUpgrade && hasGerontology) {
      return 'Continuar con Pack Completo + Gerontología';
    } else if (hasUpgrade && hasChildren) {
      return 'Continuar con Pack Completo + Infancias';
    } else if (hasUpgrade) {
      return 'Continuar con Pack Completo';
    } else if (hasGerontology && hasChildren) {
      return `Continuar con ${basePlanName} + 2 Especialidades`;
    } else if (hasGerontology) {
      return `Continuar con ${basePlanName} + Gerontología`;
    } else if (hasChildren) {
      return `Continuar con ${basePlanName} + Infancias`;
    } else {
      return `Continuar con ${basePlanName}`;
    }
  };
  const handleContinue = () => {
    let checkoutUrl = '';
    const hasUpgrade = selectedOptions.has('upgrade');
    const hasGerontology = selectedOptions.has('gerontology');
    const hasChildren = selectedOptions.has('children');
    const isProPlan = basePlanPrice === 23990;
    // Determine URL based on selected combinations
    if (hasUpgrade && hasGerontology && hasChildren) {
      // All 3 selected (from Basic plan)
      checkoutUrl = 'https://checkout.empezahoy.shop/cart/48164020781288:1';
    } else if (hasUpgrade && hasGerontology) {
      // Upgrade + Gerontology (from Basic plan)
      checkoutUrl = 'https://checkout.empezahoy.shop/cart/48164018487528:1';
    } else if (hasUpgrade && hasChildren) {
      // Upgrade + Children (from Basic plan)
      checkoutUrl = 'https://checkout.empezahoy.shop/cart/48164020355304:1';
    } else if (hasUpgrade) {
      // Upgrade only (from Basic to Pro plan $23,990)
      checkoutUrl = 'https://checkout.empezahoy.shop/cart/48110109425896:1';
    } else if (hasGerontology && hasChildren) {
      // Both specialties
      if (isProPlan) {
        // Pro plan + both specialties
        checkoutUrl = 'https://checkout.empezahoy.shop/cart/48164020781288:1';
      } else {
        // Basic plan + both specialties
        checkoutUrl = 'https://checkout.empezahoy.shop/cart/48164017242344:1';
      }
    } else if (hasGerontology) {
      // Gerontology only
      if (isProPlan) {
        // Pro plan + gerontology
        checkoutUrl = 'https://checkout.empezahoy.shop/cart/48164018487528:1';
      } else {
        // Basic plan + gerontology
        checkoutUrl = 'https://checkout.empezahoy.shop/cart/48164015210728:1';
      }
    } else if (hasChildren) {
      // Children only
      if (isProPlan) {
        // Pro plan + children
        checkoutUrl = 'https://checkout.empezahoy.shop/cart/48164020355304:1';
      } else {
        // Basic plan + children
        checkoutUrl = 'https://checkout.empezahoy.shop/cart/48164015931624:1';
      }
    } else {
      // No selection
      if (isProPlan) {
        // Pro plan only
        checkoutUrl = 'https://checkout.empezahoy.shop/cart/48110109425896:1';
      } else {
        // Basic plan only
        checkoutUrl = 'https://checkout.empezahoy.shop/cart/48111967437032:1';
      }
    }
    window.location.href = checkoutUrl;
  };
  return <AnimatePresence data-id="element-245">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" data-id="element-246" />

          {/* Popup */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-247">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto relative" data-id="element-248">
              {/* Close button */}
              <button onClick={onClose} className="absolute top-3 right-3 text-brand-muted hover:text-brand-dark transition-colors z-10" data-id="element-249">
                <X className="w-5 h-5" data-id="element-250" />
              </button>

              {/* Header - Compact */}
              <div className="text-center pt-6 pb-4 px-4" data-id="element-251">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-brand-copper/20 to-brand-copper/10 flex items-center justify-center mx-auto mb-3" data-id="element-252">
                  <Gift className="w-7 h-7 text-brand-copper" data-id="element-253" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-brand-dark mb-2" data-id="element-254">
                  ¡Mejorá tu compra!
                </h2>
                <p className="text-brand-muted text-sm md:text-base" data-id="element-255">
                  Aprovechá estas ofertas exclusivas
                </p>
              </div>

              {/* Upsell Options - Compact */}
              <div className="px-4 pb-3 space-y-2" data-id="element-256">
                {upsellOptions.map(option => <div key={option.id} onClick={() => toggleOption(option.id)} className={`cursor-pointer rounded-xl p-3 border-2 transition-all duration-300 ${selectedOptions.has(option.id) ? 'border-brand-copper bg-brand-copper/5 shadow-lg' : 'border-brand-accent/30 bg-white hover:border-brand-copper/40'}`} data-id="element-257">
                    <div className="flex items-start gap-3" data-id="element-258">
                      {/* Checkbox */}
                      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${selectedOptions.has(option.id) ? 'border-brand-copper bg-brand-copper' : 'border-brand-muted/40'}`} data-id="element-259">
                        {selectedOptions.has(option.id) && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-260">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-261" />
                          </svg>}
                      </div>

                      {/* Content */}
                      <div className="flex-grow min-w-0" data-id="element-262">
                        <h3 className="text-base font-bold text-brand-dark mb-0.5" data-id="element-263">
                          {option.title}
                        </h3>
                        <p className="text-xs text-brand-muted mb-2 line-clamp-2" data-id="element-264">
                          {option.description}
                        </p>
                        <div className="flex items-center gap-1.5" data-id="element-265">
                          <span className="text-lg font-bold text-brand-copper" data-id="element-266">
                            + ${option.price.toLocaleString('es-AR')}
                          </span>
                          <span className="text-xs text-brand-muted" data-id="element-267">ARS</span>
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>

              {/* Summary - Compact */}
              <div className="px-4 pb-3" data-id="element-268">
                <div className="bg-brand-sage/30 rounded-xl p-3 space-y-2" data-id="element-269">
                  <div className="flex items-center justify-between text-brand-dark text-sm" data-id="element-270">
                    <span className="font-medium" data-id="element-271">{basePlanName}</span>
                    <span className="font-bold" data-id="element-272">
                      ${basePlanPrice.toLocaleString('es-AR')}
                    </span>
                  </div>

                  {selectedOptions.size > 0 && <div className="border-t border-brand-accent/30 pt-2 space-y-1.5" data-id="element-273">
                      {Array.from(selectedOptions).map(optionId => {
                  const option = upsellOptions.find(o => o.id === optionId);
                  if (!option) return null;
                  return <div key={optionId} className="flex items-center justify-between text-brand-dark text-xs" data-id="element-274">
                            <span className="truncate pr-2" data-id="element-275">
                              {option.title}
                            </span>
                            <span className="font-semibold whitespace-nowrap" data-id="element-276">
                              + ${option.price.toLocaleString('es-AR')}
                            </span>
                          </div>;
                })}
                    </div>}

                  <div className="border-t-2 border-brand-copper/30 pt-2 flex items-center justify-between" data-id="element-277">
                    <span className="text-lg font-bold text-brand-dark" data-id="element-278">
                      Total
                    </span>
                    <span className="text-2xl font-bold text-brand-copper" data-id="element-279">
                      ${calculateTotal().toLocaleString('es-AR')}
                    </span>
                  </div>
                </div>
              </div>

              {/* CTA Button - Compact */}
              <div className="px-4 pb-4" data-id="element-280">
                <motion.button onClick={handleContinue} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className="w-full bg-gradient-to-r from-[#c4883a] to-[#d4943f] text-white font-bold text-base py-4 px-6 rounded-xl shadow-[0_0_25px_rgba(196,136,58,0.35)] hover:shadow-[0_0_40px_rgba(196,136,58,0.55)] transition-all duration-300 flex items-center justify-center gap-2" data-id="element-281">
                  <span data-id="element-282">👉</span>
                  <span data-id="element-283">{getButtonText()}</span>
                </motion.button>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}